import { IonButton, IonContent, IonHeader, IonImg, IonModal, IonPage, IonTextarea, useIonAlert, useIonLoading } from "@ionic/react";
import { useCallback, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import AppHeader from "../../../components/AppHeader/AppHeader";
import { useAppDispatch, useAppSelector } from "../../../services/redux/hook";
import EbioroLoadingLogo from "../../../components/EbioroLoading/EbioroLoadingLogo";
import { DelegateSigningParams, Sep10ChallengeParams, UserInfo, WalletInfoState, WalletRecoveryEmailRequestParameters } from "../../../types/types";
import { login, signTx } from "../../../services/signer/signer";
import './LoginEmailVerify.css';
import { Key } from "@cubist-labs/cubesigner-sdk";
import { receivedSep10Token, setWalletInfo } from "../../../services/redux/slices";
import { createMigrateAccountTransaction, createRemoveDeviceKeySignerTransaction } from "../../../services/stellar/TransactionBuilder";
import { Memo, MemoType, Networks, Operation, Transaction } from "@stellar/stellar-sdk";
import { stellarApi } from "../../../services/api/stellar";
import { Preferences } from "@capacitor/preferences";
import { createSep7URI } from "../../../services/stellar/sep7";
import { ebioroApi, useDelegateSigningMutation } from "../../../services/api/ebioro/ebioro";
import { useTransactionTracker } from "../../../hooks/useTransactionTracker";
import * as Sentry from "@sentry/react";
import { CapacitorKeyStore } from "../../../services/stellar/wallet-sdk/plugins/CapacitorStorage";
import { KeyManager, KeyManagerPlugins, KeyType } from "../../../services/stellar/wallet-sdk";
import { Key as StorageKey } from '../../../services/stellar/wallet-sdk/types';
import { CubeSignerClient, envs } from "@cubist-labs/cubesigner-sdk";
import { createStellarKeys } from "../../../services/stellar/stellar-utils";

const ebioroAuthDomain = import.meta.env.VITE_API_AUTH_DOMAIN || '';
const isTestnet = import.meta.env.VITE_isTestnet === 'true' ? true : false;
const cubesignerEnv = isTestnet ? envs.gamma : envs.prod;
const cubesignerOrgId = import.meta.env.VITE_CUBESIGNER_ORG_ID;

interface LoginEmailVerifyProps {
    partialToken?: string;
    closeModal?: () => void;
}

const LoginEmailVerify: React.FC<LoginEmailVerifyProps> = ({ partialToken: propPartialToken, closeModal }) => {

    const history = useHistory();
    const dispatch = useAppDispatch()
    const { t } = useTranslation();
    const user: UserInfo = useAppSelector((state) => state.user);
    const walletInfo: WalletInfoState = useAppSelector((state) => state.walletInfo);
    const pin: string = useAppSelector((state) => state.pin);
    const flowContext = useAppSelector((state) => state.flowContext);
    const [signature, setSignature] = useState<string>('');
    const [partialToken, setPartialToken] = useState<string>('');
    const [present] = useIonAlert();
    const [presentLoader, setPresentLoader] = useState(false);
    const [resendingCode, setResendingCode] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [delegateSigning] = useDelegateSigningMutation();
    
    // Ref to store transaction context for callbacks
    const transactionContextRef = useRef<{
        accountKey: string;
        device_public_key?: string;
        device_master_key?: string;
    } | null>(null);

    const secureStorage = new CapacitorKeyStore();

    const keyManager = new KeyManager({ keyStore: secureStorage });

    keyManager.registerEncrypter(KeyManagerPlugins.ScryptEncrypter);
    keyManager.setDefaultNetworkPassphrase(isTestnet ? Networks.TESTNET : Networks.PUBLIC);

    const presentErrorAlert = useCallback((error: any) => {
        present({
            header: t('application.error.HEADER'),
            message: error.data || t('application.uncaught.ERROR'),
            buttons: [
                {
                    text: t('application.error.CLOSE'),
                    role: 'cancel',
                    handler: () => {
                        setSignature('');
                        return;
                    }
                }
            ],
        });
    }, [present, t]);

    const goToInitPage = useCallback(() => {
        if (flowContext.currentFlow === 'migrate') {
            // For migration flow, close the modal using the provided function
            if (closeModal) {
                closeModal();
            }
            setPresentLoader(false);

        } else {
            // For normal login flow, go to init page
            history.push('/init');
        }
    }, [history, flowContext.currentFlow, closeModal]);

    // Encrypt keys function
    const encryptKeys = useCallback(async (accountKey: string, accountPrivatekey = '', deviceKey: string, devicePrivateKey: string) => {

        const account_key: StorageKey = {
            id: 'account_key',
            publicKey: accountKey,
            privateKey: accountPrivatekey,
            type: KeyType.plaintextKey,
            network: isTestnet ? Networks.TESTNET : Networks.PUBLIC,
        };

        const device_key: StorageKey = {
            id: 'device_key',
            publicKey: deviceKey,
            privateKey: devicePrivateKey,
            type: KeyType.plaintextKey,
            network: isTestnet ? Networks.TESTNET : Networks.PUBLIC,
        };

        const keys: StorageKey[] = [account_key, device_key];

        for (let i = 0; i < keys.length; i++) {
            await keyManager.storeKey({
                key: keys[i],
                password: pin,
                encrypterName: KeyManagerPlugins.ScryptEncrypter.name,
            })
        }

    }, [keyManager, pin]);

    // Transaction tracker callbacks
    const transactionCallbacks = useCallback(() => ({
        onCompleted: async () => {
            // Encrypt keys after successful transaction
            const context = transactionContextRef.current;
            if (!context) {
                setPresentLoader(false);
                presentErrorAlert({ data: t('login.verify.ERROR_CONTEXT') || 'Transaction context not found.' });
                return;
            }

            try {
                const currentWalletInfo = flowContext.currentFlow === 'migrate'
                    ? { 
                        ...walletInfo, 
                        device_key: context.device_public_key || null, 
                        device_private_key: context.device_master_key || null 
                      }
                    : walletInfo;

                await encryptKeys(context.accountKey, '', currentWalletInfo.device_key as string, currentWalletInfo.device_private_key as string);

                await dispatch(ebioroApi.endpoints.markKeysAsMigrated
                    .initiate({ email: user.email as string, public_key: context.accountKey as string })).unwrap()

                // Update Redux state to indicate keys are successfully stored
                const completedWalletInfo: WalletInfoState = {
                    ...currentWalletInfo,
                    account_key: context.accountKey,
                    has_key: true
                };
                dispatch(setWalletInfo(completedWalletInfo));

                // Navigate to init page after successful completion
                setTimeout(() => {
                    setPresentLoader(false);
                    goToInitPage();
                }, 1000); // Small delay to show success message
            } catch (error: any) {
                Sentry.captureException(error);
                setPresentLoader(false);
                presentErrorAlert({ data: t('login.verify.ERROR_KEY_ENCRYPTION') || 'Failed to encrypt keys. Please try again.' });
            }
        },
        onError: (error: string) => {
            // Dismiss loader and show error to user
            setPresentLoader(false);

            if (error === 'submission_error') {
                presentErrorAlert({ data: t('transaction.submission.ERROR') });
            } else if (error === 'transaction_error') {
                presentErrorAlert({ data: t('transaction.processing.ERROR') || 'Transaction failed to process. Please try again.' });
            } else {
                presentErrorAlert({ data: t('application.uncaught.ERROR') });
            }
        },
        onSurgePricing: () => {
            // Handle surge pricing if needed
        }
    }), [goToInitPage, presentErrorAlert, t, flowContext.currentFlow, walletInfo, dispatch, user.email, encryptKeys]);

    // Transaction tracker hook
    const {
        startTracking
    } = useTransactionTracker(undefined, {
        callbacks: transactionCallbacks()
    });

    const onSubmit = async () => {

        if (!signature.trim()) {
            presentErrorAlert({ data: t('login.verify.ERROR_NO_SIGNATURE') || 'Please enter the authentication code from your email' });
            return;
        }

        if (!partialToken) {
            presentErrorAlert({ data: t('login.verify.ERROR_NO_TOKEN') || 'Authentication token not found' });
            return;
        }

        setPresentLoader(true);

        try {

            // Here we create the tx to remove the old device key as signer 
            // and add the new device key as signer

            let error: any
            let tx: Transaction | null = null;
            let signerAddress: string | null = null;
            let signedXDR: string;
            let signedTx: Transaction<Memo<MemoType>, Operation[]> | null = null;
            let accountKey: string;
            const [device_public_key, device_master_key] = createStellarKeys(); // this is for the migration flow.

            if (flowContext.currentFlow !== 'migrate') {

                // Use the login function from signer.ts because the user already exists 
                // in signer service
                const client = await login(partialToken, signature);

                if (client.isErr()) {
                    setPresentLoader(false);
                    presentErrorAlert({
                        data: t('login.verify.LOGIN_FAILED') || 'Failed to login with CubeSigner. Please check your signature.'
                    });
                    return;
                }

                const keys: Key[] = await client.value.sessionKeys();

                const stellarKey = keys.map((key) => key.id.includes('Key#Stellar_') ? key : null).filter(k => k !== null);

                signerAddress = stellarKey.length > 0 ? (stellarKey[0]?.materialId || null) : null;

                if (!signerAddress) {
                    setPresentLoader(false);
                    presentErrorAlert({
                        data: 'No account found to log you in. Please contact support.'
                    });
                    return;
                }

                const metadata = await stellarKey[0]?.metadata();

                accountKey = metadata?.toString().split('alien_stellar_')[1].trim() as string;
                const newWalletInfo = { ...walletInfo };
                newWalletInfo.account_key = accountKey;

                dispatch(setWalletInfo(newWalletInfo));

                [error, tx] = await createRemoveDeviceKeySignerTransaction(
                    accountKey, // this is the account key for which we are loggin the user in
                    walletInfo.device_private_key as string, // this is the new device key we want to add as additional signer
                    [signerAddress], // this is the signer that would sign the transaction
                );

                // now we are signing the tx with the signer.ts signTx function
                if (tx) {
                    signedXDR = await signTx(tx.toXDR(), signerAddress, client.value);
                    signedTx = new Transaction(signedXDR, isTestnet ? Networks.TESTNET : Networks.PUBLIC);
                }

            }

            else {

                // we need to first create the user in the signer service..

                const completeOidcToken = partialToken + signature.trim();

                const identity_proof = await CubeSignerClient.proveOidcIdentity(
                    cubesignerEnv,
                    cubesignerOrgId,
                    completeOidcToken
                );

                const external_id = user.id;

                const result = await dispatch(ebioroApi.endpoints.createWallet.initiate({
                    chain: 'stellar',
                    identityProof: identity_proof,
                    external_id: external_id,
                    account_key: walletInfo.account_key as string
                })).unwrap();

                signerAddress = result.address;
                accountKey = walletInfo.account_key as string;
                const oldDevicePrivateKey = walletInfo.device_private_key as string;

                [error, tx] = await createMigrateAccountTransaction(accountKey, oldDevicePrivateKey, device_master_key, signerAddress, 20);

                if (tx) {
                    
                    signedTx = tx;

                    // Update wallet info with new device keys
                    const updatedWalletInfo = {
                        ...walletInfo,
                        device_key: device_public_key,
                        device_private_key: device_master_key
                    };

                    dispatch(setWalletInfo(updatedWalletInfo));
                }
            }

            if (error) {
                throw error;
            }

            if (!signedTx) {
                throw new Error('Failed to create recovery transaction');
            }

            const sep10Result = await getSep10Token();

            if (!sep10Result) {
                setPresentLoader(false);
                presentErrorAlert({ data: t('login.verify.ERROR_SEP10_AUTH') || 'Failed to authenticate with Stellar network. Please try again.' });
                return;
            }

            const uri = createSep7URI(signedTx, accountKey);

            if (!uri) {
                setPresentLoader(false);
                presentErrorAlert({ data: t('registration.secure.wallet.ERROR_URI_CREATION') });
                return;
            }

            // Store transaction context for the callback
            transactionContextRef.current = {
                accountKey,
                device_public_key: flowContext.currentFlow === 'migrate' ? device_public_key : undefined,
                device_master_key: flowContext.currentFlow === 'migrate' ? device_master_key : undefined
            };

            try {

                let params: DelegateSigningParams = {
                    uri: uri,
                    account: accountKey,
                    name: 'Wallet login',
                    msg: 'Wallet login transaction',
                }

                const response = await delegateSigning(params).unwrap();
                const stellarTxId = response.stellar_transaction_id as string;

                // Start tracking - the onCompleted callback will handle key encryption and navigation
                await startTracking(stellarTxId);
                
                // Don't execute any code after startTracking - callbacks will handle everything
                return;

            } catch (error: any) {

                Sentry.captureException(error);

                if (error.message === 'submission_error') {
                    presentErrorAlert({ data: t('transaction.submission.ERROR') });

                } else {
                    presentErrorAlert({ data: t('application.uncaught.ERROR') });
                }

                setPresentLoader(false);
                return;
            }

        } catch (error: any) {
            setPresentLoader(false);
            presentErrorAlert({
                data: t('login.verify.LOGIN_FAILED') || 'Failed to login. Please try again.'
            });
        }
    };

    useEffect(() => {
        // First check if token is passed as prop (from modal navigation)
        if (propPartialToken) {
            setPartialToken(propPartialToken);
            return;
        }

        // Get the data passed from the previous page (normal navigation)
        const location: any = history.location;
        if (location.state?.detail) {
            const { token: token } = location.state.detail;
            setPartialToken(token);

        } else {
            // Only show error if user hasn't completed login yet
            // If they have keys, they've already completed login and shouldn't be here
            if (!walletInfo.has_key && !walletInfo.account_key) {
                presentErrorAlert({
                    data: t('login.verify.ERROR_NO_DATA') || 'Login data not found'
                });
                setTimeout(() => {
                    history.goBack();
                }, 2000);
            } else {
                // User has already completed login, redirect to accounts
                history.replace('/tabs/accounts');
            }
        }
    }, [history, t, presentErrorAlert, walletInfo.has_key, walletInfo.account_key, propPartialToken]);

    //Cleanup cooldown timer on unmount
    useEffect(() => {
        return () => {
            if (resendCooldown > 0) {
                setResendCooldown(0);
            }
        };
    }, []);

    const handleSignatureChange = (event: CustomEvent) => {
        const value = event.detail.value || '';
        setSignature(value.trim());
    };

    const handleResendCode = async () => {

        if (!user?.email) {
            presentErrorAlert({ data: t('login.verify.ERROR_NO_EMAIL') || 'Email not found. Please try logging in again.' });
            return;
        }

        if (resendCooldown > 0) {
            return; // Still in cooldown period
        }

        setResendingCode(true);

        try {

            const params: WalletRecoveryEmailRequestParameters = {
                email: user.email
            }

            const response = await dispatch(ebioroApi.endpoints.recoverWalletByEmail.initiate(params)).refetch().unwrap();
            const newPartialToken = response.partial_token;

            // Update the partial token with the new one
            setPartialToken(newPartialToken);

            // Clear the current signature so user enters the new code
            setSignature('');

            // Start cooldown period (60 seconds)
            setResendCooldown(60);
            const cooldownInterval = setInterval(() => {
                setResendCooldown((prev) => {
                    if (prev <= 1) {
                        clearInterval(cooldownInterval);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);

            // Show success message
            present({
                header: t('login.verify.RESEND_SUCCESS_HEADER') || 'Code Sent',
                message: t('login.verify.RESEND_SUCCESS_MESSAGE') || 'A new verification code has been sent to your email.',
                buttons: [
                    {
                        text: t('application.error.CLOSE') || 'OK',
                        role: 'cancel'
                    }
                ]
            });

        } catch (error: any) {
            presentErrorAlert({
                data: t('login.verify.RESEND_ERROR') || 'Failed to resend verification code. Please try again.'
            });
        } finally {
            setResendingCode(false);
        }
    };

    // Get SEP-10 token for Stellar authentication
    const getSep10Token = useCallback(async (): Promise<any> => {
        // Validate all required parameters
        if (!walletInfo.account_key) {
            presentErrorAlert({ data: t('login.verify.ERROR_MISSING_ACCOUNT') || 'Missing account key. Please try again.' });
            return null;
        }

        if (!walletInfo.device_private_key) {
            presentErrorAlert({ data: t('login.verify.ERROR_MISSING_DEVICE_KEY') || 'Missing device key. Please try again.' });
            return null;
        }

        if (!ebioroAuthDomain) {
            presentErrorAlert({ data: t('login.verify.ERROR_MISSING_DOMAIN') || 'Missing authentication domain. Please check configuration.' });
            return null;
        }

        const params: Sep10ChallengeParams = {
            account: walletInfo.account_key,
            home_domain: ebioroAuthDomain,
            network: isTestnet ? Networks.TESTNET : Networks.PUBLIC,
            privateKey: walletInfo.device_private_key
        };

        try {
            const sep10Data = await dispatch(stellarApi.endpoints.sep10.initiate(params)).unwrap();

            if (sep10Data) {
                dispatch(receivedSep10Token(sep10Data));
                await Preferences.set({
                    key: ebioroAuthDomain,
                    value: JSON.stringify(sep10Data),
                });

                return sep10Data;
            }

            return null;
        } catch (error) {
            presentErrorAlert({ data: t('login.verify.ERROR_SEP10_AUTH') || 'Failed to authenticate with Stellar network. Please try again.' });
            return null;
        }
    }, [walletInfo.account_key, walletInfo.device_private_key, dispatch, t, presentErrorAlert]);


    return (

        <IonPage>

            <IonHeader className="ion-no-border">

                <AppHeader
                    title={t('login.verify.HEADER') as string}
                    goBack={true}
                >
                </AppHeader>

            </IonHeader>

            <IonContent className={`secure-wallet-content ${(presentLoader || resendingCode) ? 'content-disabled' : ''}`}>

                <div className="content-container">

                    {/* Hero Section with Icon */}
                    <div className="hero-section">
                        <div className="icon-container">
                            <div className="icon-background">
                                <IonImg
                                    src={'/assets/img/safety.svg'}
                                    className="safety-icon"
                                />
                            </div>
                            <div className="success-badge">
                                <svg className="success-icon" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                            </div>
                        </div>

                        <p className="page-description">
                            {t('login.verify.DESCRIPTION_PART1')}{' '}
                            <span className="email-highlight">{user.email}</span>.{' '}
                            {t('login.verify.DESCRIPTION_PART2')}.
                        </p>
                    </div>

                    {/* Verification Code Input */}
                    <div className="input-card">
                        <div className="input-section">
                            <label className="input-label">
                                {t('login.verify.SIGNATURE_LABEL')}
                            </label>
                            <IonTextarea
                                fill="outline"
                                placeholder={t('login.verify.SIGNATURE_PLACEHOLDER') as string}
                                mode="md"
                                autoGrow={true}
                                className="verification-input"
                                rows={3}
                                value={signature}
                                onIonInput={handleSignatureChange}
                                disabled={presentLoader}
                            />
                        </div>

                        <div className="resend-section">
                            <p className="resend-text">
                                {t('login.verify.RESEND_TEXT')}{' '}
                                <button
                                    className="resend-button"
                                    onClick={handleResendCode}
                                    disabled={presentLoader || resendingCode || resendCooldown > 0}
                                >
                                    {resendingCode
                                        ? (t('login.verify.RESENDING') || 'Sending...')
                                        : resendCooldown > 0
                                            ? `${t('login.verify.RESEND_WAIT') || 'Wait'} ${resendCooldown}s`
                                            : (t('login.verify.RESEND_BUTTON') || 'Resend Code')
                                    }
                                </button>
                            </p>
                        </div>
                    </div>

                    {/* Action Button */}
                    <div className="button-container">
                        <IonButton
                            expand="block"
                            className="verify-button"
                            color="primary"
                            onClick={onSubmit}
                            disabled={!signature.trim() || presentLoader}
                        >
                            {presentLoader ? t('login.verify.LOGGING_IN') : t('login.verify.CONTINUE_BUTTON')}
                        </IonButton>
                    </div>
                </div>


                <IonModal isOpen={presentLoader}>
                    <IonContent fullscreen={true} scrollY={false} className="loginEmailVerifyPageWaitModal_content_background">
                        <div className="loginEmailVerifyPageWaitModal_logo">
                            <EbioroLoadingLogo
                                icon_color="white"
                                mainText={t('login.verify.PROCESSING')}
                                mainTextColor="light"
                                subText=''
                                subTextColor="light"
                            />
                        </div>
                    </IonContent>
                </IonModal>
            </IonContent>

        </IonPage>
    );
};

export default LoginEmailVerify